import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";

const H2 = ({ children }) => <h2 className="font-display text-2xl md:text-3xl tracking-tight text-white mt-10 mb-4">{children}</h2>;
const H3 = ({ children }) => <h3 className="font-display text-lg md:text-xl text-white mt-6 mb-2">{children}</h3>;
const P = ({ children }) => <p className="text-white/70 leading-relaxed mb-4">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-6 text-white/70 leading-relaxed mb-4 space-y-1">{children}</ul>;

export function LegalShell({ title, subtitle, children, path, description }) {
  return (
    <div className="min-h-screen bg-[#0c0c0f] text-white">
      <Seo title={title} description={description || subtitle} path={path} />
      <Navbar />
      <main className="pt-32 pb-24 max-w-3xl mx-auto px-6 md:px-10">
        <div className="mb-10 border-b border-white/10 pb-8">
          <div className="text-[11px] font-mono-alt text-white/40 mb-2 tracking-widest uppercase">Legal</div>
          <h1 className="font-display font-light text-4xl md:text-5xl tracking-[-0.02em] leading-tight">{title}</h1>
          {subtitle && <p className="mt-3 text-white/60 max-w-xl">{subtitle}</p>}
        </div>
        <div className="text-white/70">{children}</div>
      </main>
      <Footer />
    </div>
  );
}

const EFF_DATE = "1 September 2026";
const CONTACT_LINE = "hello@wtddetailing.ca · +1 368-380-8483 · 11629 — 145 Street NW, Edmonton, AB T5M 1V9, Canada";

export function PrivacyPolicy() {
  return (
    <LegalShell
      title="Privacy Policy"
      subtitle={`Effective ${EFF_DATE}. How WTD Detailing & Tint collects, uses and protects your personal information.`}
      path="/privacy"
      description="Privacy Policy for WTD Detailing & Tint — Edmonton detailing, PPF and ceramic coating studio."
    >
      <H2>Who we are</H2>
      <P>WTD Detailing & Tint operates this website and services from 11629 — 145 Street NW, Edmonton, AB T5M 1V9, Canada. This policy explains what personal data we handle and your rights under Canadian PIPEDA and Alberta's PIPA.</P>

      <H2>Information we collect</H2>
      <UL>
        <li><strong>Contact details</strong> you submit through quote and booking forms: name, phone, email, vehicle information, service preferences.</li>
        <li><strong>Vehicle documentation</strong> during service check-in: mileage, existing damage, photographs and your signature.</li>
        <li><strong>Usage data</strong> collected automatically: IP address, browser type, pages visited, referring URLs, cookies (see our <a href="/cookies" className="text-[#1F5EFF] hover:text-white underline">Cookie Policy</a>).</li>
        <li><strong>Payment data</strong> is processed by our payment provider (Square). We do not store card numbers.</li>
      </UL>

      <H2>How we use your data</H2>
      <UL>
        <li>To provide quotes, book appointments, complete work orders and send invoices.</li>
        <li>To document the vehicle condition at intake for our and your protection.</li>
        <li>To send service-related communication (appointment confirmations, ready-for-pickup notifications, follow-ups).</li>
        <li>To respond to reviews, complaints and support requests.</li>
        <li>With your explicit consent — to send occasional marketing about new services and promotions.</li>
        <li>To comply with Canadian legal, tax and warranty obligations.</li>
      </UL>

      <H2>Legal basis</H2>
      <P>We rely on your consent (marketing), performance of a contract (fulfilling services), legal obligation (tax, warranty), and legitimate business interest (fraud prevention, dispute defence with the inspection record).</P>

      <H2>Sharing and third parties</H2>
      <P>We do not sell your personal information. We share data only with:</P>
      <UL>
        <li>Payment processor (Square Canada Inc.) — for card and e-transfer payments.</li>
        <li>Communications providers (Resend, Twilio) — to deliver email and SMS on our behalf.</li>
        <li>Warranty providers (film and coating manufacturers) — only when submitting a claim on your vehicle.</li>
        <li>Government / law enforcement — when required by valid Canadian legal process.</li>
      </UL>

      <H2>Data retention</H2>
      <P>We retain inspection records, invoices and warranty documentation for a minimum of seven years to comply with tax law and manufacturer warranties. Marketing subscribers are retained until unsubscribed. Analytics data is retained no longer than 26 months.</P>

      <H2>Your rights</H2>
      <P>You may request access, correction or deletion of your personal information. Contact us at <a href="mailto:hello@wtddetailing.ca" className="text-[#1F5EFF] hover:text-white underline">hello@wtddetailing.ca</a>. You may also file a complaint with the Office of the Information and Privacy Commissioner of Alberta or the Privacy Commissioner of Canada.</P>

      <H2>Security</H2>
      <P>Data is transmitted over HTTPS, stored on a secured server with restricted access, and inspection photographs are only accessible to authenticated staff. We conduct regular software updates and use encrypted authentication tokens.</P>

      <H2>Children</H2>
      <P>Our services are directed to vehicle owners aged 18 and over. We do not knowingly collect data from children.</P>

      <H2>Changes to this policy</H2>
      <P>We may update this policy from time to time. The effective date at the top will reflect changes. Material changes will be announced on this page.</P>

      <H2>Contact</H2>
      <P>{CONTACT_LINE}</P>
    </LegalShell>
  );
}

export function TermsOfService() {
  return (
    <LegalShell
      title="Terms of Service"
      subtitle={`Effective ${EFF_DATE}. These terms govern use of our website and services.`}
      path="/terms"
      description="Terms of Service for WTD Detailing & Tint."
    >
      <H2>Acceptance</H2>
      <P>By using this website, requesting a quote, or bringing a vehicle to our studio, you agree to these Terms. If you do not agree, please do not use the site or our services.</P>

      <H2>Services</H2>
      <P>We provide automotive detailing, paint protection film (PPF), ceramic coating, paint correction, headlight restoration, spray-on bed liner and related services. Window tint is not currently offered. Availability, pricing and film/coating options may change without notice. Written quotes are valid for 14 days unless stated otherwise.</P>

      <H2>Appointments and cancellations</H2>
      <UL>
        <li>Appointments are confirmed once you receive a confirmation from our studio.</li>
        <li>We ask for at least 24 hours notice for cancellations or reschedules.</li>
        <li>No-shows or same-day cancellations for full-day services may be subject to a deposit forfeit.</li>
      </UL>

      <H2>Vehicle inspection at drop-off</H2>
      <P>Every vehicle is photographed and inspected on arrival. Existing damage is documented and shown to you before work begins. The inspection is signed digitally. You agree that the signed inspection represents the vehicle's condition at that time.</P>

      <H2>Payment</H2>
      <P>All prices are in Canadian dollars and exclude GST unless stated. Payment is due upon completion via cash, e-transfer, or card (processed by Square). Deposits may be required for full-vehicle PPF, ceramic coating and multi-day services.</P>

      <H2>Warranties</H2>
      <UL>
        <li>Warranty coverage and duration vary by product, film line and service package. Applicable warranty terms will be confirmed before installation.</li>
        <li>Ceramic coating packages provide protection up to 2, 4 or 5 years depending on the selected coating package, vehicle use, maintenance and aftercare.</li>
        <li>Workmanship coverage varies by service. Any applicable workmanship or manufacturer warranty terms will be provided with the specific service or product.</li>
        <li>Warranties do not cover damage from accidents, chemical spills, off-road use, automated brushes, or improper maintenance.</li>
      </UL>

      <H2>Vehicle handling and liability</H2>
      <P>We treat every vehicle as our own. Vehicles are covered by our garagekeepers' insurance while in our custody. We are not liable for items of personal value left unsecured in the vehicle. Please remove valuables before drop-off.</P>

      <H2>Intellectual property</H2>
      <P>All content on this website (photographs, copy, logos, layout) is the property of WTD Detailing & Tint and may not be reproduced without written permission.</P>

      <H2>Limitation of liability</H2>
      <P>To the maximum extent permitted by Alberta law, our total liability arising out of any service is limited to the amount you paid for that service. We are not liable for indirect, incidental or consequential damages.</P>

      <H2>Governing law</H2>
      <P>These Terms are governed by the laws of the Province of Alberta and the applicable federal laws of Canada. Disputes will be resolved in the courts of Edmonton, Alberta.</P>

      <H2>Contact</H2>
      <P>{CONTACT_LINE}</P>
    </LegalShell>
  );
}

export function CookiePolicy() {
  return (
    <LegalShell
      title="Cookie Policy"
      subtitle={`Effective ${EFF_DATE}. What cookies we use and how to control them.`}
      path="/cookies"
      description="Cookie Policy for WTD Detailing & Tint."
    >
      <H2>What are cookies?</H2>
      <P>Cookies are small text files stored on your device by your browser when you visit websites. They help the site remember information about your visit.</P>

      <H2>Cookies we use</H2>
      <H3>Strictly necessary</H3>
      <UL>
        <li><code>access_token</code> — authenticated session cookie used by CRM staff to stay signed in. HttpOnly, Secure.</li>
        <li><code>wtd-cookie-consent-v1</code> — records your cookie preference so we don't ask again.</li>
      </UL>
      <H3>Analytics (optional, requires consent)</H3>
      <UL>
        <li><strong>PostHog</strong> — anonymous usage analytics: pages visited, click patterns, session duration. IP addresses are masked. Data retention: up to 26 months.</li>
      </UL>
      <H3>Marketing</H3>
      <P>We do not currently use marketing or advertising cookies. If we ever add them, they will be listed here and require your explicit consent.</P>

      <H2>Managing cookies</H2>
      <P>You can accept, decline, or clear cookies at any time. On this site the banner appears on your first visit — use "Essential only" to opt out of analytics. You can also clear your browser's cookies for wtddetailing.ca to reset the banner.</P>

      <H2>Third-party services</H2>
      <P>External providers may set their own cookies when embedded on our site (for example, if we embed a YouTube video or an Instagram feed). Consult their policies for details.</P>

      <H2>Contact</H2>
      <P>{CONTACT_LINE}</P>
    </LegalShell>
  );
}
